import emailjs from '@emailjs/browser';
import { doc, setDoc, getDoc, Timestamp, collection, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase.config';
import { EmailData, NotificationHistory, NotificationSettings } from '../types/notification.types';
import { Mold } from '../types';
import { LifeCycleAlert } from './lifeCycle.service';
import { emailjsConfig } from '../config/emailjs.config';
// import { generateReportData } from '../pages/ProductionReport'; // TODO: 페이지 생성 후 활성화

class NotificationService {
  private readonly serviceId = emailjsConfig.serviceId;
  private readonly templateId = emailjsConfig.templateId;
  private readonly publicKey = emailjsConfig.publicKey;

  constructor() {
    // EmailJS 초기화
    emailjs.init(this.publicKey);
  }

  // 이메일 발송
  async sendEmail(emailData: EmailData): Promise<boolean> {
    try {
      const response = await emailjs.send(
        this.serviceId,
        this.templateId,
        emailData
      );
      
      console.log('Email sent successfully:', response);
      await this.saveNotificationHistory(emailData, 'sent');
      return true;
    } catch (error) {
      console.error('Email send failed:', error);
      await this.saveNotificationHistory(emailData, 'failed', String(error));
      return false;
    }
  }

  // 수명 상태 알림 발송 (새로운 메서드)
  async sendLifeCycleAlert(mold: Mold, status: LifeCycleAlert): Promise<boolean> {
    const recipients = ['admin@company.com']; // 실제 환경에서는 설정에서 가져오기
    
    const emailData: EmailData = {
      to_email: recipients[0],
      to_name: '관리자',
      subject: `[EGMMS] ${status.message} - ${mold.moldNumber}`,
      message: `
        금형 정보:
        - 금형번호: ${mold.moldNumber}
        - 금형명: ${mold.moldName}
        - 고객사: ${mold.customerName}
        - 현재 수명율: ${status.percentage}%
        - 현재 쇼트: ${mold.currentShots.toLocaleString()}
        - 설계 수명: ${mold.designLife.toLocaleString()}
        
        상태: ${status.message}
      `,
      mold_number: mold.moldNumber,
      mold_name: mold.moldName,
      current_shots: mold.currentShots,
      design_life: mold.designLife,
      lifetime_percentage: status.percentage
    };

    return await this.sendEmail(emailData);
  }

  // 테스트 이메일 발송
  async sendTestEmail(toEmail: string): Promise<boolean> {
    const emailData = {
      to_email: toEmail,
      to_name: '테스트 사용자',
      from_name: 'EGMMS 시스템',
      subject: '[EGMMS] 테스트 이메일',
      message: `
EGMMS 알림 시스템 테스트 이메일입니다.

이 이메일을 받으셨다면 알림 설정이 정상적으로 작동하고 있습니다.

테스트 시간: ${new Date().toLocaleString('ko-KR')}
시스템: EGMMS (Easy Mold Management System)

감사합니다.
      `.trim()
    };

    try {
      const response = await emailjs.send(
        this.serviceId,
        this.templateId,
        emailData
      );
      
      console.log('Email sent successfully:', response);
      await this.saveNotificationHistory(emailData as any, 'sent');
      return true;
    } catch (error) {
      console.error('Email send failed:', error);
      await this.saveNotificationHistory(emailData as any, 'failed', String(error));
      return false;
    }
  }
  async sendLifetimeWarning(mold: any, recipients: string[]): Promise<void> {
    const lifetimePercentage = (mold.currentShots / mold.designLife) * 100;
    
    for (const email of recipients) {
      const emailData: EmailData = {
        to_email: email,
        to_name: '관리자',
        subject: `[EGMMS] 금형 수명 경고 - ${mold.moldNumber}`,
        message: `금형 ${mold.moldNumber}(${mold.moldName})의 수명이 ${lifetimePercentage.toFixed(1)}%에 도달했습니다.`,
        mold_number: mold.moldNumber,
        mold_name: mold.moldName,
        current_shots: mold.currentShots,
        design_life: mold.designLife,
        lifetime_percentage: lifetimePercentage
      };

      await this.sendEmail(emailData);
    }
  }

  // 점검 알림 발송
  async sendMaintenanceAlert(mold: any, recipients: string[]): Promise<void> {
    for (const email of recipients) {
      const emailData: EmailData = {
        to_email: email,
        to_name: '관리자',
        subject: `[EGMMS] 금형 점검 필요 - ${mold.moldNumber}`,
        message: `금형 ${mold.moldNumber}(${mold.moldName})의 점검이 필요합니다.`
      };

      await this.sendEmail(emailData);
    }
  }

  // 알림 히스토리 저장
  private async saveNotificationHistory(
    emailData: EmailData, 
    status: 'sent' | 'failed', 
    errorMessage?: string
  ): Promise<void> {
    try {
      // Firebase 연동 시 구현 예정
      console.log('Notification history saved:', {
        recipient: emailData.to_email,
        subject: emailData.subject,
        status,
        errorMessage
      });
    } catch (error) {
      console.error('Failed to save notification history:', error);
    }
  }

  // 알림 히스토리 조회
  async getNotificationHistory(): Promise<NotificationHistory[]> {
    // Firebase 연동 시 구현 예정
    return [];
  }

  // 수명 체크 및 자동 알림
  async checkLifetimeAndNotify(molds: any[], recipients: string[]): Promise<void> {
    for (const mold of molds) {
      const lifetimePercentage = (mold.currentShots / mold.designLife) * 100;
      
      // 90% 이상일 때 경고
      if (lifetimePercentage >= 90) {
        await this.sendLifetimeWarning(mold, recipients);
      }
      
      // 100% 이상일 때 점검 알림
      if (lifetimePercentage >= 100) {
        await this.sendMaintenanceAlert(mold, recipients);
      }
    }
  }

  // 사용자 알림 설정 저장
  async saveUserSettings(userId: string, settings: NotificationSettings): Promise<void> {
    try {
      const userSettingsRef = doc(db, 'userSettings', userId);
      await setDoc(userSettingsRef, {
        ...settings,
        updatedAt: Timestamp.now(),
        createdAt: Timestamp.now()
      }, { merge: true });
      
      console.log('User settings saved successfully');
    } catch (error) {
      console.error('Failed to save user settings:', error);
      throw new Error('알림 설정 저장에 실패했습니다.');
    }
  }

  // 사용자 알림 설정 로드
  async getUserSettings(userId: string): Promise<NotificationSettings | null> {
    try {
      const userSettingsRef = doc(db, 'userSettings', userId);
      const docSnap = await getDoc(userSettingsRef);
      
      if (docSnap.exists()) {
        return docSnap.data() as NotificationSettings;
      }
      
      // 기본값 반환
      return {
        enabled: true,
        recipients: [],
        email: '',
        lifetimeWarning: true,
        lifetimeWarningThreshold: 90,
        transactionReminder: true,
        maintenanceAlert: true,
        dailyReport: false,
        weeklyReport: true,
        customerReport: false,
        customerEmails: [],
        customerReportFrequency: 'monthly'
      };
    } catch (error) {
      console.error('Failed to load user settings:', error);
      return null;
    }
  }

  // 자동 수명 알림 체크 및 발송
  async checkAndSendLifetimeAlerts(userId: string): Promise<void> {
    console.log('📧 [알림 체크] 시작 - userId:', userId);
    
    try {
      // 1. 사용자 설정 로드
      const settings = await this.getUserSettings(userId);
      console.log('📧 [알림 체크] 사용자 설정:', settings);
      
      if (!settings || !settings.lifetimeWarning || !settings.email) {
        console.log('📧 [알림 체크] 중단: 알림 비활성화 또는 이메일 없음');
        return;
      }

      // 2. 모든 금형 조회
      const moldsRef = collection(db, 'molds');
      const moldsSnapshot = await getDocs(moldsRef);
      
      console.log('📧 [알림 체크] 금형 개수:', moldsSnapshot.size);
      
      if (moldsSnapshot.empty) {
        console.log('📧 [알림 체크] 중단: 금형 없음');
        return;
      }

      // 3. 각 금형의 수명율 체크
      const alerts: Array<{ mold: Mold; percentage: number }> = [];
      
      moldsSnapshot.forEach((doc) => {
        const mold = { id: doc.id, ...doc.data() } as Mold;
        
        // 수명율 계산
        const percentage = Math.round((mold.currentShots / mold.designLife) * 100);
        
        const threshold = settings.lifetimeWarningThreshold || 90;
        console.log(`📧 [알림 체크] ${mold.moldNumber}: ${percentage}% (임계값: ${threshold}%)`);
        
        // 임계값 도달 체크
        if (percentage >= threshold) {
          // 24시간 이내 알림 발송 여부 체크 (중복 방지)
          const lastAlert = mold.lastLifetimeAlert as any;
          const now = Date.now();
          const oneDayInMs = 24 * 60 * 60 * 1000;
          
          if (!lastAlert) {
            console.log(`  ✓ 발송 대상: 이전 알림 없음`);
            alerts.push({ mold, percentage });
          } else if ((now - lastAlert.toMillis()) > oneDayInMs) {
            console.log(`  ✓ 발송 대상: 마지막 알림 24시간 경과`);
            alerts.push({ mold, percentage });
          } else {
            console.log(`  ✗ 발송 제외: 24시간 내 알림 발송됨`);
          }
        }
      });

      // 4. 알림 발송
      if (alerts.length === 0) {
        console.log('📧 [알림 체크] 완료: 발송 대상 없음');
        return;
      }

      console.log(`📧 [알림 발송] ${alerts.length}개 금형 시작`);
      
      for (const { mold, percentage } of alerts) {
        const emailData: EmailData = {
          to_email: settings.email,
          to_name: '관리자',
          subject: `[EGMMS] 금형 수명 경고 - ${mold.moldNumber}`,
          message: `
금형 정보:
- 금형번호: ${mold.moldNumber}
- 금형명: ${mold.moldName}
- 고객사: ${mold.customerName}
- 현재 수명율: ${percentage}%
- 현재 쇼트: ${mold.currentShots.toLocaleString()}
- 설계 수명: ${mold.designLife.toLocaleString()}

⚠️ 금형 수명이 ${settings.lifetimeWarningThreshold}%를 초과했습니다.
점검 또는 교체를 검토해 주세요.

시간: ${new Date().toLocaleString('ko-KR')}
          `.trim(),
          mold_number: mold.moldNumber,
          mold_name: mold.moldName,
          current_shots: mold.currentShots,
          design_life: mold.designLife,
          lifetime_percentage: percentage
        };

        const success = await this.sendEmail(emailData);
        
        if (success) {
          // 알림 발송 시간 기록
          const moldRef = doc(db, 'molds', mold.id!);
          await setDoc(moldRef, {
            lastLifetimeAlert: Timestamp.now()
          }, { merge: true });
          
          console.log(`  ✅ 발송 완료: ${mold.moldNumber}`);
        } else {
          console.log(`  ❌ 발송 실패: ${mold.moldNumber}`);
        }
      }
      
      console.log('📧 [알림 발송] 모두 완료');
      
    } catch (error) {
      console.error('📧 [알림 체크] 오류:', error);
    }
  }

  // 출고 알림 발송
  async sendCheckOutAlert(userId: string, mold: Mold, transaction: any): Promise<void> {
    try {
      console.log('📤 [출고 알림] 시작 -', mold.moldNumber);
      
      // 사용자 설정 확인
      const settings = await this.getUserSettings(userId);
      if (!settings || !settings.transactionReminder || !settings.email) {
        console.log('📤 [출고 알림] 중단: 알림 비활성화 또는 이메일 없음');
        return;
      }

      const emailData: EmailData = {
        to_email: settings.email,
        to_name: '관리자',
        subject: `[EGMMS] 금형 출고 - ${mold.moldNumber}`,
        message: `
금형이 출고 처리되었습니다.

금형 정보:
- 금형번호: ${mold.moldNumber}
- 금형명: ${mold.moldName}
- 고객사: ${mold.customerName}

출고 정보:
- 처리자: ${transaction.userName}
- 출고 시간: ${new Date(transaction.timestamp?.toMillis()).toLocaleString('ko-KR')}
- 사용 목적: ${transaction.purpose || '-'}
- 예상 쇼트: ${transaction.shots?.toLocaleString() || '-'}
- 예상 반납일: ${transaction.expectedReturn ? new Date(transaction.expectedReturn).toLocaleDateString('ko-KR') : '-'}

현재 상태: 사용중
        `.trim(),
        mold_number: mold.moldNumber,
        mold_name: mold.moldName
      };

      const success = await this.sendEmail(emailData);
      console.log(success ? '📤 [출고 알림] ✅ 발송 완료' : '📤 [출고 알림] ❌ 발송 실패');
      
    } catch (error) {
      console.error('📤 [출고 알림] 오류:', error);
    }
  }

  // 입고 알림 발송
  async sendCheckInAlert(userId: string, mold: Mold, transaction: any): Promise<void> {
    try {
      console.log('📥 [입고 알림] 시작 -', mold.moldNumber);
      
      // 사용자 설정 확인
      const settings = await this.getUserSettings(userId);
      if (!settings || !settings.transactionReminder || !settings.email) {
        console.log('📥 [입고 알림] 중단: 알림 비활성화 또는 이메일 없음');
        return;
      }

      const emailData: EmailData = {
        to_email: settings.email,
        to_name: '관리자',
        subject: `[EGMMS] 금형 입고 - ${mold.moldNumber}`,
        message: `
금형이 입고 처리되었습니다.

금형 정보:
- 금형번호: ${mold.moldNumber}
- 금형명: ${mold.moldName}
- 고객사: ${mold.customerName}

입고 정보:
- 처리자: ${transaction.userName}
- 입고 시간: ${new Date(transaction.timestamp?.toMillis()).toLocaleString('ko-KR')}
- 실제 쇼트: ${transaction.shots?.toLocaleString() || '-'}
- 비고: ${transaction.notes || '-'}

현재 상태: 보관중
현재 누적 쇼트: ${mold.currentShots.toLocaleString()}
수명율: ${Math.round((mold.currentShots / mold.designLife) * 100)}%
        `.trim(),
        mold_number: mold.moldNumber,
        mold_name: mold.moldName,
        current_shots: mold.currentShots,
        design_life: mold.designLife,
        lifetime_percentage: Math.round((mold.currentShots / mold.designLife) * 100)
      };

      const success = await this.sendEmail(emailData);
      console.log(success ? '📥 [입고 알림] ✅ 발송 완료' : '📥 [입고 알림] ❌ 발송 실패');
      
    } catch (error) {
      console.error('📥 [입고 알림] 오류:', error);
    }
  }

  // 생산 실적 리포트 발송
  async sendProductionReport(
    recipients: string[], 
    reportData: {
      periodStart: string;
      periodEnd: string;
      totalProduction: number;
      averageDaily: number;
      maxDaily: number;
      activeMolds: number;
      moldDetails: Array<{
        moldNumber: string;
        moldName: string;
        customerName: string;
        totalShots: number;
        productionDays: number;
      }>;
    }
  ): Promise<boolean> {
    try {
      console.log('📊 [리포트 발송] 시작 - 수신자:', recipients.length);

      const reportMessage = `
==============================================
     EGMMS 생산 실적 리포트
==============================================

📅 기간: ${reportData.periodStart} ~ ${reportData.periodEnd}

📈 요약 정보
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
• 총 생산량: ${reportData.totalProduction.toLocaleString()} 쇼트
• 일평균 생산: ${reportData.averageDaily.toLocaleString()} 쇼트
• 최대 일생산: ${reportData.maxDaily.toLocaleString()} 쇼트
• 활성 금형: ${reportData.activeMolds}개

🔧 금형별 생산 실적
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
${reportData.moldDetails.map((mold, index) => `
${index + 1}. ${mold.moldNumber} - ${mold.moldName}
   고객사: ${mold.customerName}
   생산량: ${mold.totalShots.toLocaleString()} 쇼트
   가동일: ${mold.productionDays}일
`).join('')}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
발송 시간: ${new Date().toLocaleString('ko-KR')}
시스템: EGMMS (Easy Mold Management System)
      `.trim();

      let allSuccess = true;

      for (const email of recipients) {
        const emailData: EmailData = {
          to_email: email,
          to_name: '고객사',
          subject: `[EGMMS] 생산 실적 리포트 (${reportData.periodStart} ~ ${reportData.periodEnd})`,
          message: reportMessage
        };

        const success = await this.sendEmail(emailData);
        if (!success) {
          allSuccess = false;
          console.log(`  ❌ 발송 실패: ${email}`);
        } else {
          console.log(`  ✅ 발송 완료: ${email}`);
        }
      }

      console.log(allSuccess ? '📊 [리포트 발송] ✅ 모두 완료' : '📊 [리포트 발송] ⚠️ 일부 실패');
      return allSuccess;

    } catch (error) {
      console.error('📊 [리포트 발송] 오류:', error);
      return false;
    }
  }

  // 다음 리포트 발송일 계산
  calculateNextReportDate(
    frequency: 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly',
    fromDate: Date = new Date()
  ): Date {
    const nextDate = new Date(fromDate);

    switch (frequency) {
      case 'daily':
        nextDate.setDate(nextDate.getDate() + 1);
        break;
      case 'weekly':
        nextDate.setDate(nextDate.getDate() + 7);
        break;
      case 'monthly':
        nextDate.setMonth(nextDate.getMonth() + 1);
        break;
      case 'quarterly':
        nextDate.setMonth(nextDate.getMonth() + 3);
        break;
      case 'yearly':
        nextDate.setFullYear(nextDate.getFullYear() + 1);
        break;
    }

    // 자정으로 설정
    nextDate.setHours(0, 0, 0, 0);
    return nextDate;
  }

  // 리포트 발송 예약 활성화
  async activateReportSchedule(userId: string): Promise<void> {
    try {
      const settings = await this.getUserSettings(userId);
      if (!settings) throw new Error('설정을 찾을 수 없습니다.');

      const now = Timestamp.now();
      const frequency = settings.customerReportFrequency || 'monthly';
      const nextDate = this.calculateNextReportDate(frequency as 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly');

      await this.saveUserSettings(userId, {
        ...settings,
        reportScheduleActive: true,
        lastReportSent: now,
        nextReportDate: Timestamp.fromDate(nextDate)
      });

      console.log('📅 [예약 활성화] 다음 발송일:', nextDate.toLocaleString('ko-KR'));
    } catch (error) {
      console.error('📅 [예약 활성화] 오류:', error);
      throw error;
    }
  }

  // 리포트 발송 예약 비활성화
  async deactivateReportSchedule(userId: string): Promise<void> {
    try {
      const settings = await this.getUserSettings(userId);
      if (!settings) throw new Error('설정을 찾을 수 없습니다.');

      await this.saveUserSettings(userId, {
        ...settings,
        reportScheduleActive: false
      });

      console.log('📅 [예약 비활성화] 완료');
    } catch (error) {
      console.error('📅 [예약 비활성화] 오류:', error);
      throw error;
    }
  }

  // 자동 리포트 발송 체크 (로그인 시 실행)
  async checkAndSendScheduledReports(userId: string): Promise<void> {
    try {
      console.log('📊 [자동 체크] 시작 - userId:', userId);

      // 1. 사용자 설정 로드
      const settings = await this.getUserSettings(userId);
      
      if (!settings || !settings.reportScheduleActive || !settings.customerReport) {
        console.log('📊 [자동 체크] 중단: 예약 비활성화');
        return;
      }

      if (!settings.customerEmails || !settings.customerEmails.length) {
        console.log('📊 [자동 체크] 중단: 고객사 이메일 없음');
        return;
      }

      if (!settings.nextReportDate) {
        console.log('📊 [자동 체크] 중단: 다음 발송일 미설정');
        return;
      }

      // 2. 발송 시간 체크
      const now = new Date();
      const nextReportDate = settings.nextReportDate.toDate();

      console.log('📊 [자동 체크] 현재:', now.toLocaleString('ko-KR'));
      console.log('📊 [자동 체크] 다음 발송:', nextReportDate.toLocaleString('ko-KR'));

      if (now < nextReportDate) {
        console.log('📊 [자동 체크] 아직 발송 시간 아님');
        return;
      }

      // 3. 리포트 데이터 수집 (실제 데이터)
      console.log('📊 [자동 체크] 리포트 데이터 생성 시작');
      
      // 기간 계산 (주기에 따라)
      let startDate = new Date();
      const endDate = new Date();

      const frequency = settings.customerReportFrequency || 'monthly';
      switch (frequency) {
        case 'daily':
          startDate.setDate(startDate.getDate() - 1);
          break;
        case 'weekly':
          startDate.setDate(startDate.getDate() - 7);
          break;
        case 'monthly':
          startDate.setMonth(startDate.getMonth() - 1);
          break;
        case 'quarterly':
          startDate.setMonth(startDate.getMonth() - 3);
          break;
        case 'yearly':
          startDate.setFullYear(startDate.getFullYear() - 1);
          break;
      }

      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(23, 59, 59, 999);

      // TODO: ProductionReport 페이지 생성 후 활성화
      // const reportData = await generateReportData(startDate, endDate);
      const reportData: any = {
        period: { startDate, endDate },
        summary: { total: 0 },
        data: []
      };
      console.log('📊 [자동 체크] 리포트 데이터:', reportData);

      // 4. 리포트 발송
      const success = await this.sendProductionReport(
        settings.customerEmails || [],
        reportData
      );

      if (success) {
        // 5. 다음 발송일 업데이트
        const nextDate = this.calculateNextReportDate(frequency as 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly', now);
        
        await this.saveUserSettings(userId, {
          ...settings,
          lastReportSent: Timestamp.now(),
          nextReportDate: Timestamp.fromDate(nextDate)
        });

        console.log('📊 [자동 체크] ✅ 발송 완료 및 다음 일정 업데이트:', nextDate.toLocaleString('ko-KR'));
      } else {
        console.log('📊 [자동 체크] ❌ 발송 실패');
      }

    } catch (error) {
      console.error('📊 [자동 체크] 오류:', error);
    }
  }
}

export const notificationService = new NotificationService();
